import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useSelector } from "react-redux";
import { SuprSendProvider, useNotifications } from "@suprsend/react-inbox";
// Needed wherever SuprSendProvider mounts — it renders toast popups via react-toastify internally.
import "react-toastify/dist/ReactToastify.css";
import { useSuprSend } from "@/hooks/useSuprSend";
import { useIsInternalUser } from "@/hooks/useIsInternalUser";
import { featureFlags } from "@/config";
import { selectSelectedProperty } from "@/redux/slices/property";
import { getPricingRequestDetail } from "@/pages/grouppricingcalculator/ActionApis/GpcApis";
import type { PricingDecision } from "@/pages/grouppricingcalculator/ActionApis/types";
import type { Notification } from "@/pages/notifications/types";

const SUPRSEND_WORKSPACE_KEY = import.meta.env
  .VITE_SUPRSEND_WORKSPACE_KEY as string;

const ALL_STORE_ID = "all";
// Tag format matches SuprSend template: property-{{propertyId}}
const PROPERTY_TAG_PREFIX = "property-";

export type NotificationStatus =
  | "accepted"
  | "pending"
  | "declined"
  | "error"
  | "unclassified";

type NotificationStoreStatus = "disabled" | "loading" | "error" | "ready";

interface NotificationStoreValue {
  status: NotificationStoreStatus;
  activeStoreId: string | null;
  error: string | null;
  refresh: () => void;
}

const DISABLED_VALUE: NotificationStoreValue = {
  status: "disabled",
  activeStoreId: null,
  error: null,
  refresh: () => {},
};

const NotificationStoreContext =
  createContext<NotificationStoreValue>(DISABLED_VALUE);

// Consumed by NotificationBell and the full page to check readiness/active store.
export function useNotificationStore() {
  return useContext(NotificationStoreContext);
}

interface NotificationDataValue {
  notifications: Notification[];
  unreadNotifications: Notification[];
  initialLoading: boolean;
  markRead: (n_id: string) => void;
  markAllRead: () => void;
  getStatus: (n: Notification) => NotificationStatus;
  // Triggers the GPC decision fetch — deferred until actually needed. Idempotent.
  requestClassification: () => void;
}

const EMPTY_DATA_VALUE: NotificationDataValue = {
  notifications: [],
  unreadNotifications: [],
  initialLoading: true,
  markRead: () => {},
  markAllRead: () => {},
  getStatus: () => "unclassified",
  requestClassification: () => {},
};

const NotificationDataContext =
  createContext<NotificationDataValue>(EMPTY_DATA_VALUE);

// Shared notification list + decision cache — avoids re-fetching between the dropdown and full page.
export function useNotificationData() {
  return useContext(NotificationDataContext);
}

// Extracts propertyId and requestId from a notification's action URL query params.
function extractRequestInfo(
  n: Notification
): { propertyId: string; requestId: string } | null {
  const actions = n.message?.actions;
  if (!Array.isArray(actions)) return null;
  for (const action of actions) {
    const url = action?.url;
    if (!url) continue;
    try {
      const parsed = new URL(url, window.location.origin);
      const propertyId = parsed.searchParams.get("propertyId");
      const requestId = parsed.searchParams.get("requestId");
      if (propertyId && requestId) return { propertyId, requestId };
    } catch (err) {
      console.warn("extractRequestInfo: malformed action URL", url, err);
    }
  }
  return null;
}

// Renders nothing — pushes state up via callbacks so a SuprSendProvider remount can't unmount the app.
interface NotificationDataMountProps {
  storeId: string;
  classificationRequested: boolean;
  requestClassification: () => void;
  onDataChange: (value: NotificationDataValue) => void;
}

function NotificationDataMount({
  storeId,
  classificationRequested,
  requestClassification,
  onDataChange,
}: NotificationDataMountProps) {
  const { notifications, initialLoading, markRead, markAllRead } =
    useNotifications(storeId);
  const [decisionMap, setDecisionMap] = useState<
    Record<string, PricingDecision | "error" | null>
  >({});
  // Tracks fetched n_ids to avoid re-fetching on re-renders.
  const fetchedIds = useRef(new Set<string>());

  // `@suprsend/react-inbox`'s useNotifications() isn't guaranteed to return
  // referentially stable callbacks across renders. Depending on the raw
  // `markRead`/`markAllRead` directly in the onDataChange effect below caused
  // an infinite render loop: an unstable function identity re-triggers that
  // effect -> its setState in the parent (NotificationsProvider) -> the
  // parent re-renders this whole subtree -> useNotifications() runs again ->
  // a new unstable function identity -> repeat forever ("Maximum update depth
  // exceeded"). Reading the latest function via a ref and excluding these
  // wrappers from the effect's dependency array (below) breaks the loop
  // without needing a stable identity at all.
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;
  const stableMarkRead = (n_id: string) => markReadRef.current(n_id);

  const markAllReadRef = useRef(markAllRead);
  markAllReadRef.current = markAllRead;
  const stableMarkAllRead = () => markAllReadRef.current();

  useEffect(() => {
    if (!classificationRequested) return;
    if (notifications.length === 0) return;

    const toFetch = notifications.filter(
      (n) => !fetchedIds.current.has(n.n_id)
    );
    if (toFetch.length === 0) return;

    // Mark as fetched upfront to prevent duplicate calls
    toFetch.forEach((n) => fetchedIds.current.add(n.n_id));

    const items = toFetch
      .map((n) => ({ n_id: n.n_id, info: extractRequestInfo(n) }))
      .filter(
        (
          x
        ): x is {
          n_id: string;
          info: { propertyId: string; requestId: string };
        } => x.info !== null
      );

    // Notifications without extractable IDs render as "unclassified".
    if (items.length === 0) return;

    Promise.allSettled(
      items.map(({ info }) =>
        getPricingRequestDetail(info.propertyId, info.requestId)
      )
    ).then((results) => {
      setDecisionMap((prev) => {
        const next = { ...prev };
        results.forEach((result, i) => {
          // "error" marks a rejected fetch, distinct from "unclassified" (not GPC-related).
          next[items[i].n_id] =
            result.status === "fulfilled" ? result.value.decision : "error";
        });
        return next;
      });
    });
  }, [notifications, classificationRequested]);

  const getStatus = useCallback(
    (n: Notification): NotificationStatus => {
      const d = decisionMap[n.n_id];
      if (d === "RATE_ACCEPTED" || d === "MANUAL_RATE") return "accepted";
      if (d === "RATE_DENIED") return "declined";
      if (d === "error") return "error";
      if (n.n_id in decisionMap && d === null) return "pending";
      return "unclassified";
    },
    [decisionMap]
  );

  const unreadNotifications = useMemo(
    () => notifications.filter((n) => !n.seen_on),
    [notifications]
  );

  // Same instability risk as markRead/markAllRead applies to `notifications`
  // itself — SuprSend can hand back a new array reference every render even
  // when the content hasn't changed. A content fingerprint, not the array
  // reference, is what should decide whether there's actually anything new
  // to push up to the context.
  const notificationsFingerprint = useMemo(
    () => notifications.map((n) => `${n.n_id}:${n.seen_on ?? ""}`).join(","),
    [notifications]
  );

  useEffect(() => {
    onDataChange({
      notifications,
      unreadNotifications,
      initialLoading,
      markRead: stableMarkRead,
      markAllRead: stableMarkAllRead,
      getStatus,
      requestClassification,
    });
    // `notifications`/`unreadNotifications`/`stableMarkRead`/`stableMarkAllRead`
    // are intentionally read from render scope rather than listed here —
    // gating on `notificationsFingerprint` instead, since depending on their
    // raw references is what caused the infinite loop (see the comments
    // above). Now that stableMarkRead/stableMarkAllRead are plain functions
    // (a new one each render), including them here would reintroduce the
    // same loop. The effect still forwards whatever their latest values are
    // at the time it fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    notificationsFingerprint,
    initialLoading,
    getStatus,
    requestClassification,
    onDataChange,
  ]);

  return null;
}

// Bootstraps SuprSend and mounts the remountable SuprSendProvider as a
// sibling of the app, not an ancestor — property switches/refresh can't unmount it.
interface SuprSendMountProps {
  onStoreChange: (value: NotificationStoreValue) => void;
  onDataChange: (value: NotificationDataValue) => void;
  classificationRequested: boolean;
  requestClassification: () => void;
}

function SuprSendMount({
  onStoreChange,
  onDataChange,
  classificationRequested,
  requestClassification,
}: SuprSendMountProps) {
  const { identity, loading, error } = useSuprSend();
  const selectedProperty = useSelector(selectSelectedProperty);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const propertyTag = selectedProperty?.propertyId
    ? `${PROPERTY_TAG_PREFIX}${selectedProperty.propertyId}`
    : null;

  const stores = useMemo(
    () => [
      { storeId: ALL_STORE_ID, label: "All Notifications" },
      ...(propertyTag
        ? [
            {
              storeId: propertyTag,
              label: selectedProperty!.propertyId,
              query: { tags: [propertyTag] },
            },
          ]
        : []),
    ],
    [propertyTag, selectedProperty]
  );

  const activeStoreId = propertyTag ?? ALL_STORE_ID;

  const storeValue: NotificationStoreValue = useMemo(() => {
    if (!SUPRSEND_WORKSPACE_KEY) {
      return {
        status: "error",
        activeStoreId: null,
        error:
          "SuprSend workspace key is not configured. Please set VITE_SUPRSEND_WORKSPACE_KEY in your environment.",
        refresh,
      };
    }
    if (error) {
      return { status: "error", activeStoreId: null, error, refresh };
    }
    if (loading || !identity) {
      return { status: "loading", activeStoreId: null, error: null, refresh };
    }
    return { status: "ready", activeStoreId, error: null, refresh };
  }, [error, loading, identity, activeStoreId, refresh]);

  useEffect(() => {
    onStoreChange(storeValue);
  }, [storeValue, onStoreChange]);

  if (storeValue.status !== "ready" || !identity) {
    return null;
  }

  return (
    <SuprSendProvider
      key={`${activeStoreId}-${refreshKey}`}
      workspaceKey={SUPRSEND_WORKSPACE_KEY}
      subscriberId={identity.subscriberId}
      distinctId={identity.distinctId}
      stores={stores}
    >
      <NotificationDataMount
        storeId={activeStoreId}
        classificationRequested={classificationRequested}
        requestClassification={requestClassification}
        onDataChange={onDataChange}
      />
    </SuprSendProvider>
  );
}

// Gates SuprSend bootstrap on the feature flag + internal-user check.
// {children} is a sibling of SuprSendMount, not a descendant — its remount
// (property switch/refresh) can't unmount the app; state is lifted up via callbacks.
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const isInternal = useIsInternalUser();
  const enabled = featureFlags.notifications && isInternal;

  const [storeValue, setStoreValue] = useState(DISABLED_VALUE);
  const [dataValue, setDataValue] = useState(EMPTY_DATA_VALUE);
  const [classificationRequested, setClassificationRequested] = useState(false);
  const requestClassification = useCallback(
    () => setClassificationRequested(true),
    []
  );

  return (
    <NotificationStoreContext.Provider
      value={enabled ? storeValue : DISABLED_VALUE}
    >
      <NotificationDataContext.Provider
        value={enabled ? dataValue : EMPTY_DATA_VALUE}
      >
        {children}
      </NotificationDataContext.Provider>
      {enabled && (
        <SuprSendMount
          onStoreChange={setStoreValue}
          onDataChange={setDataValue}
          classificationRequested={classificationRequested}
          requestClassification={requestClassification}
        />
      )}
    </NotificationStoreContext.Provider>
  );
}
